import React, { useEffect, useState, useRef, Suspense } from 'react'
import {
    showPrompt,
    addProjectMedia,
    resolvePrompt,
    resolvePending,
} from 'actions/appActions'
import {
    CloseButton,
    Media,
    TopBar,
    Button,
    IconButton,
    Spinner,
    Input,
    DropdownMenu,
    MediaTile,
    Message,
    Toggle,
    Icon,
    TooltipLabel,
} from 'components'
import {
    ErrorMessage,
    PromptOptions,
    PromptType,
    MediaType,
    ThumbSize,
    MediaTagData,
    RootState,
    AppData,
    BuilderData,
    OrganizationData,
    MediaData,
    FieldType,
    AdminView,
    Dict,
} from 'app/types'
import { addMedia, addMediaFiles, removeMedia, pollJob, startJob, updateMedia, addMediaTag, removeMediaTag, generateMediaThumbnails, exportMediaFile, optimizeMediaFile } from 'actions/adminActions'
import { useAppDispatch, useAppSelector, useLocalStorage } from 'app/hooks'
import { getSetCached } from 'app/state'
import { FileUploader } from 'react-drag-drop-files'
import { parseData } from 'helpers/requestHandler'
import { getMediaLink } from 'helpers/media'
import SpinPreview from 'components/Spin/SpinPreview'
import * as fnc from 'helpers/fnc'
import { logger } from 'helpers/logger'
import { ModelhomeView } from 'views/ModelhomePage/ModelhomeView'
import { Checkbox } from 'components/Core/Checkbox'
import { current } from '@reduxjs/toolkit'
import { icons } from 'app/constants'
import { GenericEditor } from 'views/AdminPage/Views/GenericEditor'
import { EditorFieldType } from 'app/types'

const MAX_FILES = 15

interface MediaFieldEditorProps {
    value: string
    onChange: () => void,
    onSelect: () => void,
    onDelete: () => void,
}

function getSegment(app, builder, organization) {
    if (app) {
        return { app }
    }
    if (builder) {
        return { builder }
    }
    if (organization) {
        return { organization }
    }
    return {}
}

export function MediaFieldEditor(props: MediaFieldEditorProps) {
    const { onChange, onSelect, onDelete } = props
    const [editing, setEditing] = useState(false)
    const [value, setValue] = useState(props.value)

    function handleEdit() {
        onChange(value)
        setEditing(false)
    }

    function handleCancel() {
        setValue(props.value)
        setEditing(false)
    }

    function handleSelect() {
        onSelect()
    }

    if (editing) {
        return <div className="media-field-editor editing">
            <Input value={value} onChange={setValue} onSubmit={handleEdit} />
            <IconButton icon={icons.check} onClick={handleEdit} />
            <IconButton icon={icons.times} onClick={handleCancel} />
        </div>
    }
    return <div className="media-field-editor" onClick={onSelect ? handleSelect : () => setEditing(true)}>
        <span>{value}</span>
        <IconButton icon={icons.pen} noBg onClick={(e) => { e.stopPropagation(); setEditing(true) }} />
    </div>
}

interface MediaTagEditorProps {
    tags: MediaTagData[],
    app: AppData,
    builder: BuilderData,
    organization: OrganizationData,
    value: string,
    onSelect: () => void,
    onChange: () => void,
    onDelete: () => void,
    isDialog: boolean,
}

export function MediaTagEditor(props: MediaTagEditorProps) {
    const { tags, app, builder, organization, value, onSelect, onChange, onDelete, isDialog } = props
    const config = useAppSelector((state: RootState) => state.admin.config)
    if (!tags && !config) {
        return null
    }
    const tagMap = tags ? tags.reduce((acc, x) => ({ ...acc, [x.id]: x.name }), {}) : {}
    const items = tags.map((x) => {
        if (isDialog && false) {
            return { value: x.id, text: x.name }
        } else {
            return {
                value: x.id, element: <div className="group">
                    <Button className={`${value && value.includes(x.id) ? 'selected' : ''}`} onClick={() => handleSelect(x.id)}>{x.name}</Button>
                    <IconButton noBg icon={icons.trash} onClick={(e) => handleDelete(x)}></IconButton>
                </div>
            }
        }
    })
    if (value != null) {
        items.push({ value: null, text: 'No Tag' })
    }

    function handleSelect(x) {
        onSelect(x)
    }

    function handleChange(x) {
        onChange({ name: x, id: null })
    }

    function handleDelete(x) {
        onDelete(x)
    }

    return <DropdownMenu
        className="media-tag-editor"
        text={value && value in tagMap ? tagMap[value] : 'Tag'}
        value={value}
        items={items}
        onChange={handleSelect}
        onAdd={handleChange}
        addPlaceholder="Add Tag" />
}

interface MediaEditorProps {
    tags: MediaTagData[],
    app: AppData,
    builder: BuilderData,
    organization: OrganizationData,
    media: MediaData,
    onChange: () => void,
    onDelete: () => void,
    onAddTag: () => void,
    onRemoveTag: () => void,
    isDialog: boolean,
    onEdit?: () => void,
}
function MediaEditor(props: MediaEditorProps) {
    const { tags, app, builder, organization, media, onChange, onDelete, onAddTag, onRemoveTag, isDialog, onEdit } = props
    const [deleting, setDeleting] = useState(false)
    const [saving, setSaving] = useState(false)
    const [optimizing, setOptimizing] = useState(false)
    const dispatch = useAppDispatch()
    const [updateKey, setUpdateKey] = useState(Date.now())
    const previewRef = useRef()
    if (!media) {
        return null
    }
    const ModelPreview = media.mediaTypeId == MediaType.Model ? React.lazy(() => import('components/Spin/ModelPreview')) : null

    if (!media) {
        return <h3>Missing media</h3>
    }

    let fullscreen = previewRef.current != null && fnc.isFullscreen() == previewRef.current

    function handleDelete() {
        if (onDelete) {
            onDelete(media.id)
        }
        setDeleting(false)
    }

    function handleLightbox() {
        dispatch(showPrompt({ type: PromptType.Lightbox, app: app, media: [media.id] }))
    }

    function handleView() {
        if (previewRef.current) {
            fnc.toggleFullscreen(previewRef.current)
            setUpdateKey(Date.now())
        }
    }

    function handleDownload() {
        switch (media.mediaTypeId) {
            case MediaType.Spin:
                window.open(getMediaLink(media.link + '.zip', getSegment(app, builder, organization)), '_blank').focus()
                break
            default:
                window.open(getMediaLink(media.link, getSegment(app, builder, organization)), '_blank').focus()
        }
    }

    function handleDownloadMedia(mediaItem: any) {
        switch (mediaItem.mediaTypeId) {
            case MediaType.Spin:
                window.open(getMediaLink(mediaItem.link + '.zip', getSegment(app, builder, organization)), '_blank').focus()
                break
            default:
                window.open(getMediaLink(mediaItem.link, getSegment(app, builder, organization)), '_blank').focus()
        }
    }

    async function handleThumbnails() {
        await dispatch(generateMediaThumbnails({ media, options: getSegment(app, builder, organization) }))
        setUpdateKey(Date.now())
        // onChange({...media})
    }

    async function handleUploadThumbnail() {
        // Open file dialog and pick file
        const file = await fnc.pickFile()
        if (file) {
            await dispatch(generateMediaThumbnails({ media, options: getSegment(app, builder, organization), file }))
            setUpdateKey(Date.now())
        }
    }

    async function handleExport() {
        const ret = await dispatch(exportMediaFile({ media, options: getSegment(app, builder, organization) }))
        setUpdateKey(Date.now())
    }

    async function handleOptimize() {
        try {
            // Store original file size for comparison
            const originalSizeMB = Math.floor(100 * media.size / (1024 * 1024)) / 100

            dispatch(showPrompt({
                type: PromptType.Pending,
                title: 'Optimizing image...',
                successMessage: 'Image optimized successfully!',
                failureMessage: 'Failed to optimize image. Please try again.',
                confirm: true // Prevent auto-dismiss, require user to click "Okay"
            }))

            const result = await dispatch(optimizeMediaFile({ media, options: getSegment(app, builder, organization) }))

            if (result.payload && result.payload.success) {
                // Extract optimized file size from the response
                const optimizedMedia = result.payload.media || result.payload.debug?.updated
                const optimizedSizeMB = optimizedMedia ? Math.floor(100 * optimizedMedia.size / (1024 * 1024)) / 100 : null

                // Create success message with size comparison
                let successMessage = 'Image optimized successfully!'
                if (optimizedSizeMB && originalSizeMB !== optimizedSizeMB) {
                    const savedMB = Math.round((originalSizeMB - optimizedSizeMB) * 100) / 100
                    const savedPercent = Math.round(((savedMB / originalSizeMB) * 100) * 10) / 10 // Round to 1 decimal place
                    successMessage = `Image optimized from ${originalSizeMB}MB to ${optimizedSizeMB}MB (saved ${savedMB}MB, ${savedPercent}%)`
                } else if (optimizedSizeMB && originalSizeMB === optimizedSizeMB) {
                    successMessage = 'Image was already optimized'
                }

                // For success case, PendingDialog expects the message in result.message for custom messages
                dispatch(resolvePending({ success: true, message: successMessage }))
                setUpdateKey(Date.now())
            } else {
                dispatch(resolvePending(result.payload || { success: false, message: 'Optimization failed' }))
            }
        } catch (error) {
            dispatch(resolvePending({ success: false, message: error.message || 'Optimization failed' }))
        }

        // The optimization API already updated the server, so we just need to update the local UI state
        // Don't call onChange() as that would trigger another API call to update the server
        // Instead, the parent component should listen for successful optimization via Redux state updates
        // or we could dispatch an action to update the local media state directly

        // For now, we'll rely on the parent component to refresh its data or listen to Redux updates
        // No need to call onChange() which would make a redundant API call
    }

    function handleName(x) {
        const newMedia = { ...media }
        newMedia.name = x
        onChange(newMedia)
    }
    function handleTag(x) {
        const newMedia = { ...media, mediaTagId: x }
        onChange(newMedia)
    }

    async function addTag(x) {
        const ret = await dispatch(addMediaTag({ data: x, options: { app, builder, organization } }))
        const newMedia = { ...media, mediaTagId: ret.payload.mediaTag.id }
        onAddTag(ret.payload.mediaTag)
        onChange(newMedia)
    }

    function deleteTag(tag) {
        dispatch(showPrompt({ ...PromptOptions.DeleteData, title: `Delete tag ${tag.name}?` }))
            .then((x) => {
                if (x.payload) {
                    dispatch(removeMediaTag({ data: tag, options: { app, organization, builder } }))
                    onRemoveTag(tag)
                    if (media.mediaTagId == x.id) {
                        const newMedia = { ...media, mediaTagId: null }
                        onChange(newMedia)
                    }
                }
            })
    }
    function getOptions() {
        return <React.Fragment>
            {deleting && <div className="row media-selected-item-prompt">
                <span>
                    Delete?
                    <Button onClick={handleDelete}>Yes</Button>
                    <Button onClick={() => setDeleting(false)}>No</Button>
                </span>
            </div>}
            {!deleting && <IconButton icon={icons.trash} onClick={() => setDeleting(true)} title="Delete media" />}
            <IconButton icon={`${fullscreen ? icons.compressAlt : icons.expandAlt}`} onClick={handleView} title={fullscreen ? "Exit fullscreen" : "View fullscreen"} />
            {media.mediaTypeId != MediaType.Tour && <IconButton icon={icons.download} onClick={handleDownload} title="Download media file" />}
            {media.mediaTypeId == MediaType.Image || media.mediaTypeId == MediaType.Video && <IconButton icon={icons.redoAlt} onClick={handleThumbnails} title="Regenerate thumbnails" />}
            {media.mediaTypeId == MediaType.Video && <IconButton icon={icons.upload} onClick={handleUploadThumbnail} title="Upload custom thumbnail" />}
            {media.mediaTypeId == MediaType.File && media.link.includes('kml') && <IconButton icon={icons.export} onClick={handleExport} title="Export KML file" />}
            {media.mediaTypeId === MediaType.Image &&
                (media.link?.toLowerCase().includes('.png') ||
                    media.link?.toLowerCase().includes('.jpg') ||
                    media.link?.toLowerCase().includes('.jpeg')) &&
                <IconButton icon={icons.compressArrowsAlt} onClick={handleOptimize} title="Optimize image file size" />}
                
            {tags && tags.length > 0 && <DropdownMenu
                icon={icons.userTie}
                value={media.mediaTagId}
                onChange={handleTag}
                items={[
                    ...tags.map((t) => ({ text: t.name, value: t.id })),
                    ...(media.mediaTagId ? [{ text: 'No Tab', value: null }] : []),
                ]} />}
            {onEdit && <Button icon={icons.pen} onClick={onEdit}>Edit</Button>}
        </React.Fragment>
    }

    function getPreview() {
        switch (media.mediaTypeId) {
            default:
            case MediaType.Image:
                return <Media key={`${media.dateModified}${updateKey}`} app={app} builder={builder} organization={organization} mediaId={media.id} fallback thumb={!fullscreen} thumbSize={!fullscreen ? ThumbSize.Large : null} onClick={handleLightbox} hoverShow />
            case MediaType.Video:
                return <Media key={`${media.dateModified}${updateKey}`} app={app} builder={builder} organization={organization} mediaId={media.id} fallback thumb={!fullscreen} thumbSize={!fullscreen ? ThumbSize.Large : null} />
            case MediaType.Model:
                return <Suspense fallback={<Spinner />}><ModelPreview media={media} app={app} /></Suspense>
            case MediaType.Spin:
                return <SpinPreview media={media} app={app} fullscreen={fullscreen} />
            case MediaType.Tour:
                return <ModelhomeView app={app} mediaId={media.id} disclaimer={false} />
        }
    }

    function getMediaDetails() {
        switch (media.mediaTypeId) {
            case MediaType.Tour:
                return <React.Fragment>
                    <span>Created: {new Date(media.dateCreated).toLocaleDateString()}</span>
                    <span>Modified: {new Date(media.dateModified).toLocaleDateString()}</span>
                </React.Fragment>
                break
            default:
                return <React.Fragment>
                    <span>{Math.floor(100 * media.size / (1024 * 1024)) / 100}MB</span>
                    {media.width && media.height && <span>{media.width}x{media.height}</span>}
                    <span>Created: {new Date(media.dateCreated).toLocaleDateString()}</span>
                    <span>Modified: {new Date(media.dateModified).toLocaleDateString()}</span>
                    {media.fileCount && media.fileCount > 0 && <span>Files: {media.fileCount}</span>}
                </React.Fragment>
        }
    }

    const wrapperStyle = {}
    if (fullscreen) {
        wrapperStyle.padding = 0
        wrapperStyle.position = 'relative'
    } else if (media.mediaTypeId == MediaType.Spin) {
        wrapperStyle.paddingBottom = `${100 * media.height / media.width}%`
    }

    const tagMap = tags ? tags.reduce((acc, x) => ({ ...acc, [x.id]: x.name }), {}) : {}

    return <div className="media-selected-item">
        <div className="row media-wrapper" ref={previewRef} style={wrapperStyle}>
            {getPreview()}
        </div>
        <div className="row media-selected-item-info">
            <div className="row media-selected-item-options">
                {getOptions()}
            </div>
            <div className="column">
                <div className="media-field-static">
                    <span><strong>Name:</strong> {media.name}</span>
                </div>
                <div className="media-field-static">
                    <span><strong>Link:</strong> {media.link}</span>
                </div>
                {media.mediaTagId && tagMap[media.mediaTagId] && (
                    <div className="media-field-static">
                        <span><strong>Broker Tab:</strong> {tagMap[media.mediaTagId]}</span>
                    </div>
                )}
                {media.data && media.mediaTypeId === MediaType.Tour && (
                    <div className="media-field-static">
                        <span><strong>Disclaimer:</strong> {media.data}</span>
                    </div>
                )}
                {media.previewMediaId && (
                    <div className="media-field-static">
                        <span><strong>Preview:</strong> Custom preview set</span>
                    </div>
                )}
                <div className="media-field-static">
                    {getMediaDetails()}
                </div>
            </div>
        </div>
        {(saving || optimizing) && <div className="media-saving fadeIn">
            <Spinner showAfter={0} />
        </div>}
    </div>
}

interface UploadReviewStageProps {
    files: { file: File, preview?: string, name: string, altText: string }[]
    mediaTypeId: number
    onCancel: () => void
    onConfirm: (metadata: Dict) => void
    existingMedia?: Dict
    resize?: boolean
    onOptimizeChange?: (value: boolean) => void
    convertToJpg?: boolean
    onConvertToJpgChange?: (value: boolean) => void
}

function UploadReviewStage(props: UploadReviewStageProps) {
    const { files, mediaTypeId, onCancel, onConfirm, existingMedia = {}, resize = false, onOptimizeChange, convertToJpg = true, onConvertToJpgChange } = props
    const hasNonJpgImages = mediaTypeId === MediaType.Image && files.some(f => !/\.(jpg|jpeg)$/i.test(f.file.name))
    const [reviewData, setReviewData] = useState(() => {
        return files.reduce((acc, f, idx) => {
            const originalName = f.file.name
            const existingWithSameName = Object.values(existingMedia).find(
                m => m && m.link && m.link.endsWith(originalName)
            )
            
            return {
                ...acc,
                [idx]: { 
                    name: f.name, 
                    altText: existingWithSameName?.altText || f.altText 
                }
            }
        }, {})
    })
    const [deletedIndices, setDeletedIndices] = useState<Set<number>>(new Set())
    const [fileDimensions, setFileDimensions] = useState<{ [key: number]: { width: number; height: number } }>({})
    const [optimizedSizes, setOptimizedSizes] = useState<{ [key: number]: number | null }>({})

    // Load image dimensions when previews are available
    useEffect(() => {
        files.forEach((file, idx) => {
            if (file.preview && !fileDimensions[idx]) {
                const img = new Image()
                img.onload = () => {
                    setFileDimensions(prev => ({
                        ...prev,
                        [idx]: { width: img.width, height: img.height }
                    }))
                    // Compute actual optimized size
                    if (resize) {
                        computeOptimizedSize(idx, file.file, img.width, img.height)
                    }
                }
                img.src = file.preview
            }
        })
    }, [files, resize])

    async function computeOptimizedSize(idx: number, file: File, imgWidth: number, imgHeight: number) {
        try {
            // Load image
            const img = new Image()
            img.onload = async () => {
                const maxDim = 1920
                
                // Calculate new dimensions
                let newWidth = imgWidth
                let newHeight = imgHeight
                
                if (imgWidth > maxDim || imgHeight > maxDim) {
                    const ratio = imgWidth / imgHeight
                    if (ratio > 1) {
                        newWidth = maxDim
                        newHeight = Math.round(maxDim / ratio)
                    } else {
                        newHeight = maxDim
                        newWidth = Math.round(maxDim * ratio)
                    }
                }
                
                // Create canvas and resize
                const canvas = document.createElement('canvas')
                canvas.width = newWidth
                canvas.height = newHeight
                const ctx = canvas.getContext('2d')
                if (!ctx) return
                
                ctx.drawImage(img, 0, 0, newWidth, newHeight)
                
                // Convert to JPEG with quality 75 and get blob
                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            setOptimizedSizes(prev => ({
                                ...prev,
                                [idx]: blob.size
                            }))
                        }
                    },
                    'image/jpeg',
                    0.75
                )
            }
            img.src = URL.createObjectURL(file)
        } catch (e) {
            // Fallback to estimation if optimization fails
            console.warn('Failed to compute optimized size:', e)
        }
    }

    function formatBytes(bytes: number): string {
        if (bytes === 0) return '0 B'
        const k = 1024
        const sizes = ['B', 'KB', 'MB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
    }

    function estimateOptimizedSize(fileSize: number, dims: { width: number; height: number } | undefined): { estimated: number; savings: number; percent: number } {
        // Optimization algorithm (from backend):
        // 1. Resize to max 1920x1920 maintaining aspect ratio
        // 2. Apply quality 75 JPEG compression
        // 3. Convert PNG to JPG if no transparency
        // Typical savings: 60-80% for most images
        
        if (!dims) {
            // Conservative estimate without dimensions: 65% savings
            return {
                estimated: Math.round(fileSize * 0.35),
                savings: Math.round(fileSize * 0.65),
                percent: 65
            }
        }

        const { width, height } = dims
        const maxDim = 1920
        const needsResize = width > maxDim || height > maxDim
        
        // Base compression ratio for quality 75 JPEG: roughly 70-75% reduction
        let compressionRatio = 0.30 // Keep 30% of original size
        
        // Extra savings if resizing (dimensions reduction + compression)
        if (needsResize) {
            const scaleFactor = Math.min(maxDim / width, maxDim / height)
            const pixelReduction = scaleFactor * scaleFactor // Area scales with square of linear scale
            compressionRatio *= pixelReduction
        }
        
        const estimated = Math.round(fileSize * compressionRatio)
        const savings = fileSize - estimated
        const percent = Math.round((savings / fileSize) * 100)
        
        return { estimated, savings, percent }
    }

    function getFileInfo(fileIndex: number) {
        const file = files[fileIndex]
        const dims = fileDimensions[fileIndex]
        const fileSize = file.file.size
        const actualOptimizedSize = optimizedSizes[fileIndex]
        
        if (mediaTypeId !== MediaType.Image) {
            return `Size: ${formatBytes(fileSize)}`
        }
        
        let info = `Size: ${formatBytes(fileSize)}`
        if (dims) {
            info += ` • ${dims.width}×${dims.height}px`
        }
        
        if (resize) {
            if (actualOptimizedSize !== null && actualOptimizedSize !== undefined) {
                // Use actual optimized size
                const savings = fileSize - actualOptimizedSize
                const percent = Math.round((savings / fileSize) * 100)
                info += ` → ${formatBytes(actualOptimizedSize)} (saves ${percent}%)`
            } else {
                // Fallback to estimate while computing
                const { estimated, percent } = estimateOptimizedSize(fileSize, dims)
                info += ` → ${formatBytes(estimated)} (saves ${percent}%) *estimated`
            }
        } else {
            info += ` • No optimization`
        }
        
        return info
    }

    // Check which files would overwrite existing media
    function getOverwriteWarning(fileIndex: number) {
        const file = files[fileIndex]
        const originalName = file.file.name
        const existingWithSameName = Object.values(existingMedia).find(
            m => m && m.link && m.link.endsWith(originalName)
        )
        return existingWithSameName
    }

    function handleNameChange(index: number, name: string) {
        setReviewData(prev => ({
            ...prev,
            [index]: { ...prev[index], name }
        }))
    }

    function handleAltChange(index: number, altText: string) {
        setReviewData(prev => ({
            ...prev,
            [index]: { ...prev[index], altText }
        }))
    }

    function handleConfirm() {
        // Build metadata object keyed by filename, excluding deleted files
        const metadata = {}
        files.forEach((f, idx) => {
            if (!deletedIndices.has(idx)) {
                const fileName = f.file.name
                metadata[fileName] = reviewData[idx]
            }
        })
        onConfirm(metadata)
    }

    function handleDeleteFile(index: number) {
        setDeletedIndices(prev => {
            const newSet = new Set(prev)
            newSet.add(index)
            return newSet
        })
    }

    function handleRestoreFile(index: number) {
        setDeletedIndices(prev => {
            const newSet = new Set(prev)
            newSet.delete(index)
            return newSet
        })
    }

    return (
        <div className="upload-review-stage">
            <div className="upload-review-header">
                <div className="header-text">
                    <h3>Review & Edit Upload Information</h3>
                    <p>Customize the name and alt text for your {mediaTypeId === MediaType.Image ? 'images' : 'files'} before uploading.</p>
                </div>
                <div className="header-actions">
                    {hasNonJpgImages && (
                        <Checkbox onChange={onConvertToJpgChange} value={convertToJpg}>
                            Convert to JPG
                        </Checkbox>
                    )}
                    {mediaTypeId === MediaType.Image && (
                        <Checkbox onChange={onOptimizeChange} value={resize}>
                            Optimize Images
                        </Checkbox>
                    )}
                    <Button onClick={onCancel}>
                        Back
                    </Button>
                    <Button onClick={handleConfirm} className="primary" disabled={files.length - deletedIndices.size === 0}>
                        Upload {files.length - deletedIndices.size} {(files.length - deletedIndices.size) === 1 ? (mediaTypeId === MediaType.Image ? 'Image' : 'File') : (mediaTypeId === MediaType.Image ? 'Images' : 'Files')}
                    </Button>
                </div>
            </div>

            <div className="upload-review-grid">
                {files.map((file, idx) => {
                    const overwriteWarning = getOverwriteWarning(idx)
                    const isDeleted = deletedIndices.has(idx)
                    return (
                    <div key={`${file.file.name}-${idx}`} className={`upload-review-item ${overwriteWarning ? 'will-overwrite' : ''} ${isDeleted ? 'deleted' : ''}`}>
                        {/* Preview Section */}
                        <div className="upload-review-preview">
                            {file.preview ? (
                                <img src={file.preview} alt="Preview" />
                            ) : (
                                <div>
                                    <Icon name="fas fa-file" />
                                    <p>{file.file.name}</p>
                                </div>
                            )}
                        </div>

                        {/* Edit Section */}
                        <div className="upload-review-edit">
                            {/* Name Field */}
                            <div className="upload-review-field">
                                <label>Name</label>
                                <input
                                    type="text"
                                    value={reviewData[idx].name}
                                    onChange={(e) => handleNameChange(idx, e.target.value)}
                                    placeholder="Name for this file"
                                    disabled={isDeleted}
                                />
                            </div>

                            {/* Alt Text Field - only for images */}
                            {mediaTypeId === MediaType.Image && (
                                <div className="upload-review-field">
                                    <TooltipLabel tooltip="Alt text describes what the image shows. It's important for SEO and accessibility - screen readers use it to help visually impaired users understand images. Keep it concise (125 characters or less) and descriptive. Example: 'Modern kitchen with stainless steel appliances and granite countertops'" style={{ transform: 'translate(-3px, -3px)' }}>
                                        Alt Text (SEO)
                                    </TooltipLabel>
                                    <input
                                        type="text"
                                        value={reviewData[idx].altText}
                                        onChange={(e) => handleAltChange(idx, e.target.value)}
                                        placeholder="Describe the image for SEO..."
                                        disabled={isDeleted}
                                    />
                                </div>
                            )}

                            {/* Original Filename Reference */}
                            <p className="upload-review-filename">Filename: {file.file.name}</p>
                            {/* File Info */}
                            <p className="upload-review-file-info">{getFileInfo(idx)}</p>
                            {overwriteWarning && (
                                <div className="upload-review-warning">
                                    <Icon icon="fas fa-exclamation-triangle" />
                                    <span>This will overwrite existing media</span>
                                </div>
                            )}
                            {/* Delete/Restore Actions */}
                            <div className="upload-review-item-actions">
                                {isDeleted ? (
                                    <IconButton icon="fas fa-undo" onClick={() => handleRestoreFile(idx)}/>
                                ) : (
                                    <IconButton icon="fas fa-trash" onClick={() => handleDeleteFile(idx)} className="delete-btn"/>
                                )}
                            </div>
                        </div>
                        {isDeleted && (
                            <div className="deleted-overlay"></div>
                        )}
                    </div>
                    )
                })}
            </div>
        </div>
    )
}

interface MediaDialogProps {
    app: AppData,
    builder: BuilderData,
    organization: OrganizationData,
    onClose?: () => void,
    onSelect: () => void,
    onDisableClose: () => void,
    animation?: string,
    selected: string,
    multi: boolean,
    upload: boolean,
    readonly?: boolean,
    mediaFilter?: (media: any) => boolean,
    onMediaClick?: (media: any) => void,
    getDownloadIcon?: (media: any) => string,
    initialTagFilter?: string,
    noHeader?: boolean,
    fileTableLayout?: boolean,
    showTitle?: boolean,
    showMediaTypeToggle?: boolean,
    showTagDropdown?: boolean,
    mediaTagFilter?: (tag: MediaTagData) => boolean,
    showTagBadges?: boolean,
    brokerPortalMode?: boolean,
}
const fileTypes = {
    [MediaType.Image]: ['jpeg', 'jpg', 'png', 'gif', 'svg'],
    [MediaType.Model]: ['obj'],
    [MediaType.Spin]: ['zip'],
    [MediaType.File]: ['pdf', 'kml'],
}
const POLL_INTERVAL = 1000
export function MediaDialog(props: MediaDialogProps) {
    const {
        app,
        builder,
        organization,
        onClose,
        onSelect,
        onDisableClose,
        animation,
        selected,
        multi,
    } = {
        multi: false,
        ...props,
    }
    let mediaTypeId = [MediaType.Image, MediaType.Video, MediaType.Tour, MediaType.Spin, MediaType.File]
    if (props.mediaTypeId != null) {
        mediaTypeId = props.mediaTypeId
    }
    const config = useAppSelector((state: RootState) => state.admin.config)
    let allMedia = useAppSelector((state: RootState) => state.app.media)

    const [tags, setTags] = useState([])
    const [selectedMedia, setSelectedMedia] = useState((() => {
        if (!selected) {
            return {}
        }
        if (Array.isArray(selected)) {
            return selected.reduce((acc, x) => ({ ...acc, [x]: x }), {})
        }
        return { [selected]: selected }
    })())
    const [selectedMap, setSelectedMap] = useState({ ...selectedMedia })
    const [uploading, setUploading] = useState(props.upload)
    const [deleting, setDeleting] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(null)
    const [jobProgress, setJobProgress] = useState(null)
    const [job, setJob] = useState(null)
    const [error, setError] = useState(null)
    const [media, setMedia] = useState({})
    const [mediaCount, setMediaCount] = useState(null)
    const [tagFilter, setTagFilter] = useState(props.initialTagFilter || null)
    const [searchFilter, setSearchFilter] = useState(null)
    const [selectedFilter, setSelectedFilter] = useState(false)
    const [currentMediaTypeId, setCurrentMediaTypeId] = useLocalStorage('mediaTypeId', mediaTypeId || MediaType.Image)
    const [resize, setResize] = useState(true)
    const [convertToJpg, setConvertToJpg] = useLocalStorage('mediaConvertToJpg', true)
    const [viewMode, setViewMode] = useLocalStorage('mediaViewMode', 'grid') // 'grid' or 'list'
    const optimisticTabChanges = useRef<Record<string, any>>({})
    const [listOrder, setListOrder] = useState(['dateCreated', true]) // [field, isDescending]
    const [editingMedia, setEditingMedia] = useState(null) // Media being edited
    const [uploadReviewFiles, setUploadReviewFiles] = useState<{ file: File, preview?: string, name: string, altText: string }[]>([])
    const [reviewingUpload, setReviewingUpload] = useState(false)
    const dispatch = useAppDispatch()
    const isDialog = onClose != null
    const listRef = useRef()

    useEffect(() => {
        let newTags = []
        if (app) {
            newTags = app.meta.mediaTags
        } else if (builder) {
            newTags = builder.mediaTags
        } else if (organization) {
            newTags = organization.mediaTags
        }
        if (props.mediaTagFilter) {
            newTags = newTags?.filter(props.mediaTagFilter) || []
        }
        setTags(newTags)
    }, [app?.mediaTags, builder?.mediaTags, organization?.mediaTags])


    function handleAddTag(x) {
        const newTag = { name: x }
        dispatch(addMediaTag({ data: newTag, options: getSegment(app, builder, organization) }))
            .then((result) => {
                if (result.payload && result.payload.success) {
                    const createdTag = result.payload.mediaTag
                    setTags([...tags, createdTag])
                }
            })
    }

    function handleRemoveTag(x) {
        dispatch(removeMediaTag({ data: x, options: getSegment(app, builder, organization) }))
            .then((result) => {
                if (result.payload && result.payload.success) {
                    setTags(tags.filter(y => y.id != x.id))
                }
            })
    }

    useEffect(() => {
        if (Array.isArray(mediaTypeId) && !mediaTypeId.includes(currentMediaTypeId)) {
            setCurrentMediaTypeId(mediaTypeId[0])
        } else if (!Array.isArray(mediaTypeId) && currentMediaTypeId != mediaTypeId) {
            if (Array.isArray(mediaTypeId)) {
                if (mediaTypeId.length > 0) {
                    setCurrentMediaTypeId(mediaTypeId[0])
                }
            } else {
                setCurrentMediaTypeId(mediaTypeId)
            }
        }
    }, [mediaTypeId])

    useEffect(() => {
        if (currentMediaTypeId != null) {
            return
        }

        let newMediaType = null
        if (selected) {
            let selMedia = null
            if (Array.isArray(selected) && selected.length > 0) {
                selMedia = allMedia[selected[0]]
            } else if (!Array.isArray(selected)) {
                selMedia = allMedia[selected]
            }
            newMediaType = selMedia?.mediaTypeId
        } else if (!newMediaType && mediaTypeId) {
            if (Array.isArray(mediaTypeId)) {
                newMediaType = mediaTypeId[0]
            } else {
                newMediaType = mediaTypeId
            }
        }

        setCurrentMediaTypeId(newMediaType)
    }, [selected])

    useEffect(() => {
        getSetCached('media_search_filter', searchFilter, setSearchFilter, '')
    }, [searchFilter])

    useEffect(() => {
        if (tagFilter) {
            const allMediaArray = Object.values(media)
            const taggedMedia = allMediaArray.filter((m: any) => m.mediaTagId === tagFilter)
            const mediaByType = {}
            taggedMedia.forEach((m: any) => {
                if (!mediaByType[m.mediaTypeId]) {
                    mediaByType[m.mediaTypeId] = []
                }
                mediaByType[m.mediaTypeId].push(m)
            })
            console.log(`[MediaDialog] Tag filter changed to: ${tagFilter}`)
            console.log(`  - Total media in app: ${allMediaArray.length}`)
            console.log(`  - Media with this tag: ${taggedMedia.length}`)
            console.log(`  - Breakdown by type:`, mediaByType)
            console.log(`  - Current mediaTypeId filter: [${Array.isArray(mediaTypeId) ? mediaTypeId.join(', ') : mediaTypeId}]`)
        }
    }, [tagFilter])

    useEffect(() => {
        getSetCached('media_tag_filter', tagFilter, setTagFilter, '')
    }, [tagFilter])

    useEffect(() => {
        if (!currentMediaTypeId) {
            return
        }
        const newMedia = Object.keys(allMedia).filter(x => {
            const m = allMedia[x]
            if (!m) {
                return false
            }
            if (!m.link) {

            }
            if (m.link?.startsWith('download-')) {
                return false
            }
            if (!selectedFilter && !tagFilter && m.mediaTypeId != currentMediaTypeId) {
                return false
            }

            if (organization && !app && m.appId != null) {
                return false
            }

            if (m.link?.includes('-download')) {
                return false
            }

            if (mediaTypeId == MediaType.EmailResource) {
                return true
            } else {
                if (app) {
                    return m.appId == app.meta.id
                } else if (builder) {
                    return m.builderId == builder.id
                } else if (organization) {
                    return m.organizationId == organization.id
                } else {
                    return m.appId == null
                }
            }
        }).map((x) => allMedia[x]).reduce((acc, x) => {
            // Apply any pending optimistic tab changes
            if (optimisticTabChanges.current[x.id] !== undefined) {
                // If Redux already has the updated value, clear the override
                if (allMedia[x.id]?.mediaTagId === optimisticTabChanges.current[x.id]) {
                    delete optimisticTabChanges.current[x.id]
                    return { ...acc, [x.id]: x }
                }
                return { ...acc, [x.id]: { ...x, mediaTagId: optimisticTabChanges.current[x.id] } }
            }
            return { ...acc, [x.id]: x }
        }, {})
        setMedia(newMedia)
        setMediaCount(Object.keys(newMedia).length)
    }, [allMedia, currentMediaTypeId, selectedFilter, tagFilter])

    useEffect(() => {
        if (onDisableClose) {
            onDisableClose(uploadProgress != null)
        }
    }, [uploadProgress])

    useEffect(() => {
        if (!job || !uploading || error) {
            return
        }

        const interval = setInterval(() => {
            dispatch(pollJob(job.id))
                .then((x) => {
                    if (x.payload && x.payload.success) {
                        // Is job done? 
                        const { job } = x.payload
                        handleJobProgress(job.progress)
                        if (job.status == 'failed') {
                            setError({ error: job.data })
                            setJob(null)
                        } else if (job.status == 'complete') {
                            clearInterval(interval)

                            const data = parseData(JSON.parse(job.data))
                            const { media: newMedia, failures } = data

                            dispatch(addProjectMedia(newMedia))

                            // Add media to selected
                            let newSelected = {}
                            if (multi) {
                                newSelected = { ...selectedMedia }
                                Object.keys(newMedia).forEach((y) => {
                                    newSelected[newMedia[y].id] = newMedia[y].id
                                })
                                setSelectedMedia(newSelected)
                            } else if (Object.keys(newMedia).length > 0) {
                                const first = Object.keys(newMedia)[0]
                                newSelected[newMedia[first].id] = newMedia[first].id
                                setSelectedMedia(newSelected)
                            }

                            // Finish
                            if (failures && Object.keys(failures).length > 0) {
                                setError({ failures })
                                setJob(null)
                            } else {
                                handleJobComplete(newSelected)
                            }
                        }
                        // Otherwise keep checking
                    } else {
                        if (x.payload && x.payload.message) {
                            setError({ error: x.payload.message })
                        } else {
                            setError({ error: ErrorMessage.MediaUploadError })
                        }
                        setJob(null)
                    }
                }).catch(logger.error)
        }, POLL_INTERVAL)

        return () => {
            clearInterval(interval)
        }
    }, [job, error, uploading])

    function handleCurrentMediaType(x) {
        setCurrentMediaTypeId(x)
        // setSelectedMedia(multi ? [] : null)
    }

    function toggleSort(key: string) {
        if (listOrder[0] === key) {
            setListOrder([listOrder[0], !listOrder[1]])
        } else {
            setListOrder([key, true])
        }
        if (listRef.current) {
            listRef.current.scrollTop = 0
        }
    }

    function getSortHeader(key: string, label: string) {
        let downIcon = 'fas fa-chevron-down'
        let upIcon = 'fas fa-chevron-up'
        if (listOrder[0] === key) {
            if (listOrder[1]) {
                downIcon = 'fas fa-chevron-circle-down'
            } else {
                upIcon = 'fas fa-chevron-circle-up'
            }
        }
        return <th key={key} field={key} onClick={() => toggleSort(key)} className="sort-header noselect" data-field={key}>
            <div className="sort-wrapper">
                <span className="sort-label">{label}</span>
                <div className="sort-buttons">
                    <Icon noBg icon={upIcon} />
                    <Icon noBg icon={downIcon} />
                </div>
            </div>
        </th>
    }

    function sortMedia(a, b) {
        const mA = media[a]
        const mB = media[b]

        // Check whether part of original selected map
        const selectedA = selectedMap[a]
        const selectedB = selectedMap[b]
        if (selectedA && !selectedB) return -1 // selectedA comes first
        if (!selectedA && selectedB) return 1 // selectedB comes first

        // Grid mode: always sort by date modified (newest first)
        if (viewMode === 'grid') {
            const dateA = new Date(mA.dateModified)
            const dateB = new Date(mB.dateModified)
            return dateB.getTime() - dateA.getTime() // Descending order (newest first)
        }

        // List mode: sort by the selected field
        let valueA, valueB
        switch (listOrder[0]) {
            case 'name':
                valueA = mA.name.toLowerCase()
                valueB = mB.name.toLowerCase()
                break
            case 'size':
                valueA = mA.size || 0
                valueB = mB.size || 0
                break
            case 'dimensions':
                valueA = (mA.width || 0) * (mA.height || 0)
                valueB = (mB.width || 0) * (mB.height || 0)
                break
            case 'dateCreated':
                valueA = new Date(mA.dateCreated)
                valueB = new Date(mB.dateCreated)
                break
            case 'dateModified':
                valueA = new Date(mA.dateModified)
                valueB = new Date(mB.dateModified)
                break
            default:
                valueA = new Date(mA.dateCreated)
                valueB = new Date(mB.dateCreated)
        }

        // Apply sort direction for list mode
        if (valueA < valueB) return listOrder[1] ? 1 : -1  // listOrder[1] = true means descending
        if (valueA > valueB) return listOrder[1] ? -1 : 1
        return 0
    }

    function handleClose() {
        if (uploadProgress == null || error) {
            onClose()
        }
    }

    function handleSelectMedia(x, e, force = false) {
        let newMedia = { ...selectedMedia }
        if (multi || (!isDialog && (e.ctrlKey || e.shiftKey))) {
            if (e.shiftKey) {
                const mediaKeys = Object.keys(media)
                // If shift, find min index and re-build selection to all items in between
                const indexMap = mediaKeys.sort(sortMedia).reduce((acc, x, ix) => ({ ...acc, [media[x].id]: ix }), {})
                const sorted = Object.keys(newMedia).sort((a, b) => {
                    return indexMap[a] - indexMap[b]
                })
                let min = indexMap[sorted[0]]
                let max = indexMap[sorted[sorted.length - 1]]

                const clickedIndex = indexMap[x.id]
                if (clickedIndex < min) {
                    min = clickedIndex
                } else if (clickedIndex > min) {
                    max = clickedIndex
                }

                // Select all between min and max
                newMedia = {}
                for (let i = min; i <= max; i += 1) {
                    newMedia[media[mediaKeys[i]].id] = media[mediaKeys[i]].id
                }
            } else if (x.id in newMedia) {
                delete newMedia[x.id]
            } else {
                newMedia[x.id] = x.id
            }
        } else {
            if (x.id in newMedia) {
                delete newMedia[x.id]
            } else {
                newMedia = { [x.id]: x.id }
            }
        }
        setSelectedMedia(newMedia)
        if (force) {
            handleSelect()
        }
    }

    function handleStartUpload() {
        setUploadProgress(null)
        setJobProgress(null)
        setError(null)
        setUploading(true)
    }

    function handleUploadProgress(x) {
        setUploadProgress(100 * x.loaded / x.total)
    }

    function handleJobProgress(x) {
        setJobProgress(x)
    }

    function handleJobComplete(x) {
        setJob(null)
        setJobProgress(100)

        if (props.upload) {
            setTimeout(() => {
                handleSelect(x)
            }, 1000)
        } else {


            // Finish
            setTimeout(() => {
                setUploadProgress(null)
                setJobProgress(null)
                setUploading(false)
            }, 1000)
        }
    }


    function handleAddTour(link) {
        if (Object.values(allMedia).find((x) => {
            return x.mediaTypeId == MediaType.Tour && x.link == link && ((app && x.appId == app?.meta.id) || (organization && x.organizationId == organization.id) || (builder && x.builderId == builder?.id))
        }) != null) {
            const debug = Object.values(allMedia).find((x) => {
                return x.mediaTypeId == MediaType.Tour && x.link == link && ((app && x.appId == app?.meta.id) || (organization && x.organizationId == organization.id) || (builder && x.builderId == builder?.id))
            })

            setError({ error: 'Tour has already been added' })
            return
        }

        // Create new tour media and add media
        const tourMedia = {
            tempId: fnc.uniqueId(),
            id: null,
            link,
            name: link,
            mediaTypeId: MediaType.Tour,
        }
        const options =
            dispatch(addMedia({ media: tourMedia, options: getSegment(app, builder, organization) }))
                .then((x) => {
                    if (x.payload) {
                        dispatch(addProjectMedia([x.payload.media]))
                    }
                })

        setUploading(false)
    }

    async function handleAddVideo(link) {
        if (Object.values(allMedia).find((x) => {
            return x.mediaTypeId == MediaType.Video && x.app?.meta.id == app?.meta.id && x.link == link
        }) != null) {
            setError({ error: 'Video has already been added' })
            return
        }

        // Create new video media and add media
        const videoMedia = {
            tempId: fnc.uniqueId(),
            id: null,
            link,
            name: link,
            mediaTypeId: MediaType.Video,
        }
        setUploadProgress(0)
        setJobProgress(0)
        await new Promise((resolve) => setTimeout(resolve, 100))
        setUploadProgress(50)
        setJobProgress(50)
        await dispatch(addMedia({ media: videoMedia, options: getSegment(app, builder, organization) }))
            .then((x) => {
                if (x.payload) {
                    dispatch(addProjectMedia([x.payload.media]))
                }
            })
        setUploadProgress(100)
        setJobProgress(100)
        setTimeout(() => {
            setUploadProgress(null)
            setJobProgress(null)
            setUploading(false)
        }, 1000)
    }

    function handleAddMedia(files) {
        setError(null)
        const filesArray = Array.from(files).slice(0, MAX_FILES)
        
        // Only show review for images, videos, and files (not tours)
        if (currentMediaTypeId !== MediaType.Tour) {
            // Generate previews for review stage
            const reviewFiles = filesArray.map((file: File) => {
                let preview = undefined
                
                // Generate preview URLs for images
                if (currentMediaTypeId === MediaType.Image) {
                    preview = URL.createObjectURL(file)
                }
                
                return {
                    file,
                    preview,
                    name: file.name.replace(/\.[^/.]+$/, ''), // filename without extension
                    altText: ''
                }
            })
            
            setUploadReviewFiles(reviewFiles)
            setReviewingUpload(true)
            return
        }
        
        // For tours, proceed directly with upload
        performMediaUpload(filesArray, {})
    }

    function performMediaUpload(files: File[], metadata: Dict) {
        // Immediately show progress view with 0%
        setUploadProgress(0)
        setJobProgress(null)
        setError(null)
        setReviewingUpload(false)
        setUploadReviewFiles([])
        setUploading(true)
        
        // Now dispatch the actual upload
        dispatch(addMediaFiles({ files, onUploadProgress: handleUploadProgress, options: { ...getSegment(app, builder, organization), mediaTypeId: currentMediaTypeId, resize, convertToJpg }, metadata }))
            .then((x) => {
                if (x.payload && x.payload.success) {
                    const { job } = x.payload
                    setUploadProgress(100)

                    setJob(job)
                    dispatch(startJob(job.id))
                } else {
                    setError({ error: x.payload.message })
                    setUploading(false)
                }
            })
            .catch((err) => {
                setError({ error: err.message || 'Upload failed' })
                setUploading(false)
            })
    }

    async function handleDelete(x = null) {
        if (x && x in selectedMedia) {
            setDeleting(true)
            // delete media[x]
            delete selectedMedia[x]
            setSelectedMedia({ ...selectedMedia })
            await dispatch(removeMedia({ media: [x], options: { app, builder, organization } }))
            setDeleting(false)
        }
    }

    async function handleDeleteAll() {
        const count = Object.keys(selectedMedia).length
        dispatch(showPrompt({ type: PromptType.Confirm, title: `Delete ${count} selected media?`, subtitle: 'Cannot be undone' }))
            .then(async (x) => {
                if (x.payload) {
                    setDeleting(true)
                    await dispatch(removeMedia({ media: Object.keys(selectedMedia), options: { app, builder, organization } }))
                    setDeleting(false)
                    setSelectedMedia({})
                }
            })
    }

    function handleChange(x) {
        dispatch(updateMedia({ data: x, options: { app, builder, organization } }))
    }

    function handleSelect(_selectedMedia = null) {
        if (_selectedMedia == null) {
            _selectedMedia = selectedMedia
        }
        if (isDialog && !onSelect) {
            dispatch(resolvePrompt(Object.keys(_selectedMedia)))
        }

        if (onSelect) {
            onSelect(Object.keys(_selectedMedia))
        }

        if (onClose) {
            onClose()
        }
    }

    async function handleOptimizeAll() {
        const selectedKeys = Object.keys(selectedMedia)
        const optimizableMedia = selectedKeys.filter((key) => {
            const m = media[key]
            return m.mediaTypeId === MediaType.Image &&
                (m.link?.toLowerCase().includes('.png') ||
                    m.link?.toLowerCase().includes('.jpg') ||
                    m.link?.toLowerCase().includes('.jpeg'))
        })

        if (optimizableMedia.length === 0) {
            dispatch(showPrompt({
                type: PromptType.Alert,
                title: 'No Optimizable Images',
                subtitle: 'Selected media contains no PNG, JPG, or JPEG images to optimize.'
            }))
            return
        }

        dispatch(showPrompt({ type: PromptType.Confirm, title: `Optimize ${optimizableMedia.length} image(s)?`, subtitle: 'This may take a few minutes' }))
            .then(async (x) => {
                if (x.payload) {
                    await optimizeImages(optimizableMedia)
                }
            })
    }

    async function optimizeImages(optimizableMedia: string[]) {
        dispatch(showPrompt({
            type: PromptType.Pending,
            title: `Optimizing ${optimizableMedia.length} image(s)...`,
            successMessage: 'All images optimized successfully!',
            failureMessage: 'Some images failed to optimize.',
            confirm: true
        }))

        // Optimize all images in parallel using Promise.all
        const optimizationPromises = optimizableMedia.map((key) => {
            const m = media[key]
            const originalSize = m.size

            return dispatch(optimizeMediaFile({ media: m, options: getSegment(app, builder, organization) }))
                .then((result) => {
                    if (result.payload && result.payload.success) {
                        const optimizedMedia = result.payload.media || result.payload.debug?.updated
                        const saved = originalSize - (optimizedMedia?.size || originalSize)
                        return {
                            success: true,
                            media: m,
                            saved,
                            originalSize,
                            optimizedSize: optimizedMedia?.size || originalSize
                        }
                    }
                    return {
                        success: false,
                        media: m,
                        saved: 0,
                        error: result.payload?.message || 'Optimization failed'
                    }
                })
                .catch((error) => {
                    return {
                        success: false,
                        media: m,
                        error: error.message || 'Unknown error occurred',
                        saved: 0
                    }
                })
        })

        const results = await Promise.all(optimizationPromises)

        // Calculate totals and failures
        const successCount = results.filter(r => r.success).length
        const failureCount = results.length - successCount
        const totalSaved = results.reduce((acc, r) => acc + (r.saved || 0), 0)
        const totalSavedMB = Math.round((totalSaved / (1024 * 1024)) * 100) / 100
        const failures = results.filter(r => !r.success)

        // Build detailed message
        let message = ''
        let isSuccess = successCount > 0

        if (successCount === results.length) {
            // All succeeded
            message = `Successfully optimized all ${successCount} image(s)`
            if (totalSavedMB > 0) {
                message += ` (saved ${totalSavedMB}MB total)`
            }
        } else if (successCount === 0) {
            // All failed
            message = `Failed to optimize all ${results.length} image(s)`
            isSuccess = false
        } else {
            // Partial success
            message = `Optimized ${successCount} of ${results.length} image(s)`
            if (totalSavedMB > 0) {
                message += ` (saved ${totalSavedMB}MB total)`
            }
            message += `\n${failureCount} image(s) failed`
        }

        // Add failure details if any
        if (failures.length > 0 && failures.length <= 5) {
            const failureDetails = failures.map(f => `• ${f.media.name}: ${f.error}`).join('\n')
            message += `\n\nFailed images:\n${failureDetails}`
        } else if (failures.length > 5) {
            message += `\n\nFirst 5 failures:`
            const failureDetails = failures.slice(0, 5).map(f => `• ${f.media.name}: ${f.error}`).join('\n')
            message += `\n${failureDetails}\n... and ${failures.length - 5} more`
        }

        dispatch(resolvePending({
            success: isSuccess,
            message: message
        }))
    }

    function handleTagFilter(tag) {
        if (tagFilter && tagFilter.length > 0 && tagFilter === tag) {
            setTagFilter('')
        } else {
            setTagFilter(tag)
        }
    }

    function handleEditMedia(mediaId) {
        const mediaToEdit = media[mediaId]
        if (mediaToEdit) {
            setEditingMedia(mediaToEdit)
        }
    }

    function handleBackFromEdit() {
        setEditingMedia(null)
    }

    function handleSaveMedia(updatedMedia) {
        dispatch(updateMedia({ data: updatedMedia, options: { app, builder, organization } }))
            .then(() => {
                setEditingMedia(null)
            })
    }

    function handleDeleteFromEdit() {
        if (editingMedia) {
            dispatch(showPrompt({ type: PromptType.Confirm, title: 'Delete media?', subtitle: 'Cannot be undone' }))
                .then(async (x) => {
                    if (x.payload) {
                        await dispatch(removeMedia({ media: [editingMedia.id], options: { app, builder, organization } }))
                        setEditingMedia(null)
                    }
                })
        }
    }

    function getMedia() {
        let scrolledTo = false
        let items = Object.keys(media).filter((x) => {
            const m = media[x]
            
            if (selectedMedia && m.id in selectedMedia) {
                return true
            }

            if (tagFilter) {
                // When filtering by tag, skip mediaFilter - include all items with the tag
                const matchesTag = m.mediaTagId == tagFilter || (m.mediaTagId == null && tagFilter == 'untagged')
                if (!matchesTag) return false
                // Apply media type filter when tag filtering in non-broker-portal context
                if (!props.readonly && currentMediaTypeId != null && m.mediaTypeId != currentMediaTypeId) {
                    return false
                }
                // Still apply search filter within tag results
                if (searchFilter && searchFilter.length > 0) {
                    return m.name.toLowerCase().includes(searchFilter.toLowerCase())
                }
                return true
            }

            // Apply custom media filter if provided (e.g., for broker portal) - only when not filtering by tag
            if (props.mediaFilter && !props.mediaFilter(m)) {
                return false
            }

            // Apply media type filter when not filtering by tag (regular view mode)
            if (!tagFilter && currentMediaTypeId != null && m.mediaTypeId != currentMediaTypeId) {
                return false
            }

            if (searchFilter && searchFilter.length > 0) {
                return m.name.toLowerCase().includes(searchFilter.toLowerCase())
            }
            if (selectedFilter) {
                return selectedMedia && m.id in selectedMedia
            }
            return true
        })
            .sort(sortMedia)
        
        // Debug logging for tag filtering
        if (tagFilter) {
            const taggedItems = items.filter((x) => {
                const m = media[x]
                return m.mediaTagId == tagFilter
            })
            const untaggedItems = items.filter((x) => {
                const m = media[x]
                return m.mediaTagId == null && tagFilter == 'untagged'
            })
            console.log(`[MediaDialog] Tag filter active: ${tagFilter}`)
            console.log(`  - Tagged items: ${taggedItems.length}`, taggedItems.map((x) => {
                const m = media[x]
                return { id: m.id, name: m.name, mediaTypeId: m.mediaTypeId, mediaTagId: m.mediaTagId }
            }))
            console.log(`  - Untagged items: ${untaggedItems.length}`, untaggedItems.map((x) => {
                const m = media[x]
                return { id: m.id, name: m.name, mediaTypeId: m.mediaTypeId, mediaTagId: m.mediaTagId }
            }))
            console.log(`  - Total items matching filter: ${items.length}`)
        }
        
        items = items.map((x) => {
                const m = media[x]
                if (!(m.id in allMedia)) {
                    return null
                }
                const isSelected = !scrolledTo && Array.isArray(selected) ? selected.includes(m.id) : m.id == selected
                if (isSelected) {
                    scrolledTo = true
                }

                // List view rendering
                if (viewMode === 'list' && (tagFilter || currentMediaTypeId !== MediaType.File)) {
                    return (
                        <tr key={`${m.id}${m.name}`} className={`media-list-row ${selectedMedia && m.id in selectedMedia ? 'selected' : ''}`} onClick={!props.readonly ? (e) => handleSelectMedia(m, e) : (props.onMediaClick ? () => props.onMediaClick(m) : undefined)} style={props.onMediaClick ? { cursor: 'pointer' } : undefined}>
                            <td className="media-thumbnail">
                                {m.mediaTypeId === MediaType.File ? (
                                    <div className="media-file-icon-thumb">
                                        <i className={m.link?.toLowerCase().endsWith('.pdf') ? 'fas fa-file-pdf' : 'fas fa-file'} style={{ fontSize: '24px', color: m.link?.toLowerCase().endsWith('.pdf') ? '#d32f2f' : '#666' }} />
                                    </div>
                                ) : (
                                    <Media
                                        app={app}
                                        builder={builder}
                                        organization={organization}
                                        mediaId={m.id}
                                        thumb
                                        thumbSize={ThumbSize.Small}
                                        fallback
                                        hoverShow={true}
                                    />
                                )}
                            </td>
                            <td className="media-name">{m.name}</td>
                            <td className="media-size">
                                {m.mediaTypeId !== MediaType.Tour && m.size ?
                                    `${Math.floor(100 * m.size / (1024 * 1024)) / 100}MB` :
                                    '-'
                                }
                            </td>
                            <td className="media-dimensions">
                                {m.width && m.height ? `${m.width}x${m.height}` : '-'}
                            </td>
                            <td className="media-created">{new Date(m.dateCreated).toLocaleDateString()}</td>
                            <td className="media-modified">{new Date(m.dateModified).toLocaleDateString()}</td>
                            {props.readonly ? (
                                <td className="media-download">
                                    {props.brokerPortalMode && props.onMediaClick ? (
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <IconButton tertiary icon="fas fa-external-link-alt" onClick={() => props.onMediaClick(m)} title="Open in new tab" />
                                            <IconButton tertiary icon={props.getDownloadIcon ? props.getDownloadIcon(m) : icons.download} onClick={() => {
                                                const mediaUrl = getMediaLink(m.link, getSegment(app, builder, organization))
                                                fnc.downloadURI(mediaUrl, m.name || 'download')
                                            }} title="Download" />
                                        </div>
                                    ) : (
                                        <IconButton tertiary icon={props.getDownloadIcon ? props.getDownloadIcon(m) : icons.download} onClick={() => props.onMediaClick ? props.onMediaClick(m) : handleDownloadMedia(m)} title="Download/Open" />
                                    )}
                                </td>
                            ) : isDialog && (
                                <td className="media-checkbox">
                                    <Checkbox
                                        value={selectedMedia && m.id in selectedMedia}
                                        onChange={(checked) => handleSelectMedia(m, { ctrlKey: true })}
                                    />
                                </td>
                            )}
                        </tr>
                    )
                }

                // Grid view (existing MediaTile)
                let handleTileClick = null
                let tileDownloadHandler = null
                
                if (props.brokerPortalMode && props.readonly && props.onMediaClick) {
                    // In broker portal mode with readonly, split handlers:
                    // onClick = open in new tab, onDownload = download file
                    handleTileClick = () => props.onMediaClick(m)
                    tileDownloadHandler = () => {
                        const mediaUrl = getMediaLink(m.link, getSegment(app, builder, organization))
                        fnc.downloadURI(mediaUrl, m.name || 'download')
                    }
                } else {
                    // Regular logic
                    handleTileClick = props.onMediaClick ? () => props.onMediaClick(m) : (props.readonly ? () => {} : handleSelectMedia)
                    if (props.readonly && props.onMediaClick) {
                        tileDownloadHandler = () => props.onMediaClick(m)
                    } else if (props.onMediaClick) {
                        tileDownloadHandler = null
                    } else if (props.readonly) {
                        tileDownloadHandler = () => handleDownloadMedia(m)
                    }
                }
                
                const downloadIcon = props.getDownloadIcon ? props.getDownloadIcon(m) : undefined
                const showBadges = props.showTagBadges !== undefined ? props.showTagBadges : !props.noHeader
                return <MediaTile key={`${m.id}${m.name}`} app={app} builder={builder} organization={organization} media={m} onClick={handleTileClick} scrollTo={isSelected} selected={selectedMedia && m.id in selectedMedia} isDialog={isDialog} listRef={listRef} onDownload={tileDownloadHandler} downloadIcon={downloadIcon} tags={tags} showTagBadges={showBadges} fileTableLayout={props.fileTableLayout} brokerPortalMode={props.brokerPortalMode} />
            })

        // Return table for list view
        if (viewMode === 'list' && (tagFilter || currentMediaTypeId !== MediaType.File)) {
            return (
                <div className="media-list scrollable" data-view="list" ref={listRef}>
                    <table ref={listRef} className="media-list-table">
                        <thead>
                            <tr>
                                <th>Preview</th>
                                {getSortHeader('name', 'Name')}
                                {getSortHeader('size', 'Size')}
                                {getSortHeader('dimensions', 'Dimensions')}
                                {getSortHeader('dateCreated', 'Created')}
                                {getSortHeader('dateModified', 'Modified')}
                                {props.readonly ? <th>Action</th> : isDialog && <th>Select</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {items}
                        </tbody>
                    </table>
                </div>
            )
        }

        // File type already has table format, grid view for other types
        if (!tagFilter && currentMediaTypeId === MediaType.File && (props.fileTableLayout !== false)) {
            return <table ref={listRef} className="media-list scrollable">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Date Modified</th>
                        <th>Date Created</th>
                        <th>Tag</th>
                        {isDialog && <th>Selected</th>}
                    </tr>
                </thead>
                <tbody>
                    {items}
                </tbody>
            </table>
        }

        // Grid view
        return <div className="media-list scrollable" ref={listRef}>
            {items}
        </div>
    }

    async function handleDownloadAll() {
        dispatch(showPrompt({ type: PromptType.Confirm, title: `Download ${Object.keys(selectedMedia).length} media files?` }))
            .then((x) => {
                if (x.payload) {
                    const files = Object.keys(selectedMedia).map((key) => {
                        const m = media[key]
                        if (!m) return null
                        let url = ''
                        switch (m.mediaTypeId) {
                            case MediaType.Spin:
                                url = getMediaLink(m.link + '.zip', getSegment(app, builder, organization))
                                break
                            default:
                                url = getMediaLink(m.link, getSegment(app, builder, organization))
                        }
                        return { uri: url, name: m.name || `download` }
                    }).filter(f => f !== null)
                    fnc.downloadMultipleURIs(files, 100)
                }
            })
    }

    async function handleTabSelected(tagId) {
        const keys = Object.keys(selectedMedia)
        // Optimistically update local state immediately
        const updatedMedia = { ...media }
        keys.forEach((key) => {
            if (updatedMedia[key] && updatedMedia[key].mediaTagId != tagId) {
                updatedMedia[key] = { ...updatedMedia[key], mediaTagId: tagId }
                // Track optimistic change so it persists across re-renders from Redux
                optimisticTabChanges.current[key] = tagId
            }
        })
        setMedia(updatedMedia)
        // Then dispatch all API calls
        keys.forEach((key) => {
            const m = media[key]
            if (m && m.mediaTagId != tagId) {
                dispatch(updateMedia({ data: { ...m, mediaTagId: tagId }, options: { app, builder, organization } }))
            }
        })
    }

    function getSelected() {
        if (!selectedMedia) {
            return null
        }
        const count = Object.keys(selectedMedia).length
        
        // Check if any selected media are optimizable images
        const hasOptimizableImages = Object.keys(selectedMedia).some((key) => {
            const m = media[key]
            return m && m.mediaTypeId === MediaType.Image &&
                (m.link?.toLowerCase().includes('.png') ||
                    m.link?.toLowerCase().includes('.jpg') ||
                    m.link?.toLowerCase().includes('.jpeg'))
        })
        
        if (count === 0) {
            return <React.Fragment>
                <h4>Select Media</h4>
                <div className="row media-options">
                    {(isDialog || onSelect) && <Button icon={icons.check} secondary onClick={() => handleSelect()}>Select Media</Button>}
                </div>
            </React.Fragment>
        }

        if (count === 1) {
            const singleKey = Object.keys(selectedMedia)[0]
            return <React.Fragment>
                <MediaEditor
                    key={singleKey}
                    tags={tags}
                    app={app}
                    builder={builder}
                    organization={organization}
                    media={media[singleKey]}
                    onDelete={handleDelete}
                    onChange={handleChange}
                    onAddTag={handleAddTag}
                    onRemoveTag={handleRemoveTag}
                    isDialog={isDialog}
                    onEdit={() => handleEditMedia(singleKey)}
                />
                <div className="row media-options">
                    {(isDialog || onSelect) && <Button icon={icons.check} secondary onClick={() => handleSelect()}>Select Media</Button>}
                </div>
            </React.Fragment>
        }

        // Multiple selection: grouped view
        return <React.Fragment>
            <div className="media-selected-item">
                <div className="row media-wrapper media-collection-preview">
                    <div className="media-collection-stack">
                        {Object.keys(selectedMedia).slice(0, 5).map((key, index) => {
                            const m = media[key]
                            if (!m) return null
                            return <div key={key} className="media-collection-card" style={{ zIndex: 5 - index }}>
                                {m.mediaTypeId === MediaType.File ? (
                                    <div className="media-file-icon-thumb">
                                        <i className={m.link?.toLowerCase().endsWith('.pdf') ? 'fas fa-file-pdf' : 'fas fa-file'} style={{ color: m.link?.toLowerCase().endsWith('.pdf') ? '#d32f2f' : '#666' }} />
                                    </div>
                                ) : (
                                    <Media app={app} builder={builder} organization={organization} mediaId={m.id} thumb thumbSize={ThumbSize.Small} fallback />
                                )}
                            </div>
                        })}
                        <div className="media-collection-count">
                            <span>{count}</span>
                        </div>
                    </div>
                </div>
                <div className="row media-selected-item-info">
                    <div className="row media-selected-item-options">
                        <IconButton icon={icons.trash} onClick={handleDeleteAll} title={`Delete ${count} items`} />
                        <IconButton icon={icons.download} onClick={handleDownloadAll} title={`Download ${count} items`} />
                        {hasOptimizableImages && <IconButton icon={icons.compressArrowsAlt} onClick={handleOptimizeAll} title={`Optimize images`} />}
                        {tags && tags.length > 0 && <DropdownMenu
                            icon={icons.userTie}
                            onChange={handleTabSelected}
                            items={[
                                ...tags.map((t) => ({ text: t.name, value: t.id })),
                                { text: 'No Tab', value: null },
                            ]} />}
                    </div>
                    <div className="column">
                        <div className="media-field-static">
                            <span><strong>{count} items selected</strong></span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="row media-options">
                {(isDialog || onSelect) && <Button icon={icons.check} secondary onClick={() => handleSelect()}>Select Media</Button>}
            </div>
        </React.Fragment>
    }

    function getFilters() {
        let text = ''
        if (tagFilter) {
            if (tagFilter === 'untagged') {
                text = 'Broker Tab: untagged'
            } else {
                const tag = tags.find((x) => x.id == tagFilter)
                if (tag) {
                    text = `Broker Tab: ${tag.name}`
                } else {
                    text = 'Broker Tab not found'
                }
            }
        } else {
            text = 'Broker Tabs'
        }
        const isAdmin = !props.readonly && config
        
        // Build items list
        let items = []
        if (isAdmin) {
            // Add header for admin mode
            items.push({
                value: '__header__',
                element: <li className="media-tag-admin-header" onClick={(e) => e.stopPropagation()}>
                    <span className="media-tag-admin-name-col">Broker Portal Tab</span>
                    <span className="media-tag-admin-delete-col"></span>
                </li>
            })
            // Add tag items
            items = items.concat(tags ? tags.map((x) => {
                return {
                    value: x.id,
                    element: <li key={x.id} className={`media-tag-admin-row${tagFilter === x.id ? ' selected' : ''}`} onClick={(e) => {
                        e.stopPropagation()
                        if (e.target instanceof HTMLElement && !e.target.closest('button')) {
                            handleTagFilter(x.id)
                        }
                    }}>
                        <span className="media-tag-admin-name-col">{x.name}</span>
                        <div className="media-tag-admin-delete-col"><IconButton noBg icon={icons.times} onClick={(e) => {
                            e.stopPropagation()
                            dispatch(showPrompt({ ...PromptOptions.DeleteData, title: `Delete tab ${x.name}?` }))
                                .then((result) => {
                                    if (result.payload) {
                                        dispatch(removeMediaTag({ data: x, options: { app, organization, builder } }))
                                        setTags(tags.filter(t => t.id !== x.id))
                                        if (media.mediaTagId == x.id) {
                                            const newMedia = { ...media, mediaTagId: null }
                                            onChange(newMedia)
                                        }
                                    }
                                })
                        }} /></div>
                    </li>
                }
            }) : [])
        } else {
            // Non-admin mode: simple text items
            items = tags ? tags.map((x) => ({ text: x.name, value: x.id })) : []
        }
        
        if (tagFilter) {
            items.push({ text: 'clear', value: '' })
        }
        return <React.Fragment>
            {(props.showMediaTypeToggle !== false) && !selectedFilter && Array.isArray(mediaTypeId) && <DropdownMenu text={currentMediaTypeId && currentMediaTypeId != null ? `Media Type: ${MediaType[currentMediaTypeId]}` : 'Select Media Type'} value={currentMediaTypeId} onChange={handleCurrentMediaType} items={mediaTypeId.map((x) => ({ text: MediaType[x], value: x }))} />}
            {searchFilter != null && <Input placeholder="Search" value={searchFilter} onChange={setSearchFilter} clear />}
            {!props.readonly && <Checkbox value={selectedFilter} onChange={setSelectedFilter}>Selected</Checkbox>}
            {(props.showTagDropdown !== false) && <DropdownMenu
                icon={icons.userTie}
                text={text}
                buttonClass="alt"
                onAdd={!props.readonly ? handleAddTag : undefined}
                onChange={handleTagFilter}
                value={tagFilter}
                items={items} />}
        </React.Fragment>
    }

    function getUploader() {
        // Show review stage if files are pending review
        if (reviewingUpload && uploadReviewFiles.length > 0) {
            return (
                <UploadReviewStage
                    files={uploadReviewFiles}
                    mediaTypeId={currentMediaTypeId}
                    existingMedia={media}
                    resize={resize}
                    onOptimizeChange={setResize}
                    convertToJpg={convertToJpg}
                    onConvertToJpgChange={setConvertToJpg}
                    onCancel={() => {
                        setReviewingUpload(false)
                        setUploadReviewFiles([])
                        // Cleanup preview URLs
                        uploadReviewFiles.forEach(f => {
                            if (f.preview) URL.revokeObjectURL(f.preview)
                        })
                    }}
                    onConfirm={(metadata) => {
                        // Get indices of files to keep based on metadata keys
                        const uploadedFileNames = Object.keys(metadata)
                        const filesToUpload = uploadReviewFiles.filter(f => uploadedFileNames.includes(f.file.name)).map(f => f.file)
                        // Cleanup preview URLs before upload
                        uploadReviewFiles.forEach(f => {
                            if (f.preview) URL.revokeObjectURL(f.preview)
                        })
                        performMediaUpload(filesToUpload, metadata)
                    }}
                />
            )
        }

        if (error) {
            return <div className="media-uploader">
                <h3>Error uploading media</h3>
                {error.failures && <span>{error.failures.map((x) => {
                    if ('name' in x) {
                        return `${x.name}: ${JSON.stringify(x.error)}`
                    } else {
                        return JSON.stringify(x)
                    }
                }).join(',')}</span>}
                {error.error && <span>{error.error}</span>}
            </div>
        }
        if (uploadProgress != null) {
            const progress = (uploadProgress != null ? uploadProgress * 0.5 : 0) + (jobProgress != null ? jobProgress * 0.5 : 0)
            return <div className="media-uploader">

                <h3 className={jobProgress == 100 ? 'animate__animated animate__headShake' : ''}>{`${uploadProgress < 100 ? 'Uploading' : (jobProgress < 100 ? 'Processing' : 'Done!')} (${parseInt(progress)}%)`}</h3>
                <Spinner showAfter={0} show={true} />
                <div className="progress" style={{ width: `${progress}%` }} />
            </div>
        }

        switch (currentMediaTypeId) {
            case MediaType.Tour:
                return <div className="file-picker media-picker">
                    <Input placeholder="Enter tour link" autoFocus onSubmit={handleAddTour} submit />
                </div>
            case MediaType.Video:
                return <div className="file-picker media-picker">
                    <Input placeholder="Enter video link" autoFocus onSubmit={handleAddVideo} submit />
                </div>
            default:
                return <div className="file-picker media-picker">
                    <FileUploader classes="drag-and-drop" multiple handleChange={handleAddMedia} name="file" types={fileTypes[currentMediaTypeId]}>
                        <h3> Click or drag to upload files (max {MAX_FILES} files) </h3>
                    </FileUploader>
                </div>
        }
    }

    // Render edit view if editing
    if (editingMedia) {
        return (
            <div className={`media-browser ${animation ? animation : ''}`} onMouseDown={(e) => e.stopPropagation()}>
                <MediaEditView
                    app={app}
                    builder={builder}
                    organization={organization}
                    media={editingMedia}
                    tags={tags}
                    onBack={handleBackFromEdit}
                    onSave={handleSaveMedia}
                    onDelete={handleDeleteFromEdit}
                    onAddTag={handleAddTag}
                    onRemoveTag={handleRemoveTag}
                />
            </div>
        )
    }

    return <React.Fragment>
        <div className={`media-browser ${animation ? animation : ''}`} onMouseDown={(e) => e.stopPropagation()}>
            {!props.noHeader && <TopBar>
                <div className="options left">
                    {(props.showTitle !== false) && <h4>{app ? `${app.meta.name} ${MediaType[currentMediaTypeId]?.toReadable()}s` : `${MediaType[currentMediaTypeId]?.toReadable()}`}</h4>}
                </div>
                <div className="options right">
                    {!uploading && currentMediaTypeId !== MediaType.File && (
                        <Toggle
                            items={[
                                { text: 'Grid', value: 'grid', icon: icons.gallery },
                                { text: 'List', value: 'list', icon: icons.hamburger },
                            ]}
                            value={viewMode}
                            onChange={setViewMode}
                            toggleOff={false}
                            arrowFocus={false}
                            selectedClass="selected"
                        />
                    )}
                    {!uploading && getFilters()}
                    {!uploading && !props.readonly && <Button alt icon={icons.plus} onClick={handleStartUpload}>Add {MediaType[currentMediaTypeId]?.toReadable()}</Button>}
                    {uploading && !props.upload && <Button alt icon={icons.times} onClick={() => setUploading(false)}>Cancel</Button>}
                    {onClose && <CloseButton fadeIn onClick={handleClose} />}
                </div>
            </TopBar>}
            <div className="row">
                {mediaCount > 0 && <React.Fragment>
                    <div className={`column ${props.readonly ? '' : ''}`}>
                        {getMedia()}
                    </div>
                    {!props.readonly && <div className="column col-3 media-selected scrollable">
                        {getSelected()}
                    </div>}
                </React.Fragment>}
                {mediaCount == 0 && <h3>No {currentMediaTypeId in MediaType ? MediaType[currentMediaTypeId]?.toReadable().toLowerCase() : 'Media'}s added</h3>}
                {uploading && getUploader()}
                {deleting && <div className="media-deleting fadeIn">
                    <Spinner showAfter={0}></Spinner>
                </div>}
            </div>
        </div>
    </React.Fragment>
}

// Dynamic function that generates field definitions based on media data
function createMediaFieldsDef(media: MediaData, tags: MediaTagData[]) {
    const isVideoOrTour = media.mediaTypeId === MediaType.Video || media.mediaTypeId === MediaType.Tour
    
    return {
        title: null,
        noDelete: true,
        tooltip: 'Edit media properties',
        fields: {
            name: {
                type: EditorFieldType.String,
                label: 'Name',
                default: ''
            },
            link: {
                type: isVideoOrTour ? EditorFieldType.String : EditorFieldType.Fixed,
                label: 'Link',
            },
            subtitle: {
                type: EditorFieldType.String,
                label: 'Subtitle',
            },
            altText: {
                type: EditorFieldType.String,
                label: 'Alt Text (SEO)',
                placeholder: 'Describe the image for accessibility and SEO',
                default: ''
            },
            mediaTagId: {
                type: EditorFieldType.Enum,
                label: 'Broker Tab',
                default: null,
                enum: () => {
                    return [
                        { id: null, name: 'No Tab' },
                        ...tags.map(x => ({ id: x.id, name: x.name }))
                    ]
                }
            },
            data: {
                type: EditorFieldType.TextField,
                label: 'Disclaimer',
                default: '',
                hidden: media.mediaTypeId !== MediaType.Tour
            },
            previewMediaId: {
                type: EditorFieldType.Media,
                label: 'Preview Image',
                mediaTypeId: MediaType.Image,
                default: null,
            }
        }
    }
}

const mediaEditDef = {
    title: null,
    fields: {
        media: { type: EditorFieldType.Array, default: [], noDelete: true }
    }
}

function MediaEditView(props: MediaEditViewProps) {
    const { app, builder, organization, media, tags, onBack, onSave, onDelete, onAddTag, onRemoveTag } = props
    const [editedMedia, setEditedMedia] = useState({ ...media, _tags: tags })
    const [hasChanges, setHasChanges] = useState(false)
    const [saving, setSaving] = useState(false)
    const dispatch = useAppDispatch()

    // Generate field definitions dynamically based on media data
    const mediaFieldsDef = createMediaFieldsDef(editedMedia, tags)

    useEffect(() => {
        const changes = JSON.stringify(editedMedia) !== JSON.stringify({ ...media, _tags: tags })
        setHasChanges(changes)
    }, [editedMedia, media, tags])

    function handleChange(field, value) {
        setEditedMedia(prev => ({ ...prev, [field]: value }))
        setHasChanges(true)
    }

    function handleSubmit() {
        return new Promise((resolve) => {
            setSaving(true)
            const { _tags, ...mediaToSave } = editedMedia
            onSave(mediaToSave)
            // Keep saving state visible briefly to show feedback
            setTimeout(() => {
                setSaving(false)
                resolve(true)
            }, 500)
        })
    }

    function handleBack() {
        if (hasChanges) {
            dispatch(showPrompt({ ...PromptOptions.UnsavedChanges }))
                .then((x) => {
                    if (x.payload) {
                        onBack()
                    }
                })
        } else {
            onBack()
        }
    }

    function checkChange() {
        return new Promise((resolve) => {
            if (hasChanges) {
                dispatch(showPrompt({ ...PromptOptions.UnsavedChanges }))
                    .then((x) => {
                        if (x.payload) {
                            resolve(true)
                        } else {
                            resolve(false)
                        }
                    })
            } else {
                resolve(true)
            }
        })
    }

    function handleValidation() {
        const errors = []
        if (!editedMedia.name || editedMedia.name.trim().length === 0) {
            errors.push('Media name is required')
        }
        return errors.length > 0 ? errors : null
    }

    function getPreview() {
        const previewStyle = { maxHeight: '400px', width: '100%', objectFit: 'contain' as const }

        switch (media.mediaTypeId) {
            case MediaType.Image:
                return <Media
                    app={app}
                    builder={builder}
                    organization={organization}
                    mediaId={media.id}
                    fallback
                    style={previewStyle}
                />
            case MediaType.Video:
                return <Media
                    app={app}
                    builder={builder}
                    organization={organization}
                    mediaId={media.id}
                    fallback
                    thumb
                    thumbSize={ThumbSize.ExtraLarge}
                    style={previewStyle}
                />
            case MediaType.Tour:
                return <ModelhomeView
                    app={app}
                    mediaId={media.id}
                    disclaimer={false}
                    style={previewStyle}
                />
            case MediaType.Spin:
                return <SpinPreview
                    media={media}
                    app={app}
                    style={previewStyle}
                />
            default:
                return <div style={previewStyle} className="media-preview-placeholder">
                    <Icon icon={icons.file} size="large" />
                    <span>{media.name}</span>
                </div>
        }
    }

    function getMediaInfo() {
        return (
            <div className="media-info-grid">
                <div className="info-item">
                    <span className="label">Type:</span>
                    <span className="value">{MediaType[media.mediaTypeId]}</span>
                </div>
                {media.size && (
                    <div className="info-item">
                        <span className="label">Size:</span>
                        <span className="value">{Math.floor(100 * media.size / (1024 * 1024)) / 100}MB</span>
                    </div>
                )}
                {media.width && media.height && (
                    <div className="info-item">
                        <span className="label">Dimensions:</span>
                        <span className="value">{media.width}x{media.height}</span>
                    </div>
                )}
                <div className="info-item">
                    <span className="label">Created:</span>
                    <span className="value">{new Date(media.dateCreated).toLocaleDateString()}</span>
                </div>
                <div className="info-item">
                    <span className="label">Modified:</span>
                    <span className="value">{new Date(media.dateModified).toLocaleDateString()}</span>
                </div>
                {media.fileCount && media.fileCount > 0 && (
                    <div className="info-item">
                        <span className="label">Files:</span>
                        <span className="value">{media.fileCount}</span>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className="media-edit-view">
            <TopBar>
                <div className="options left">
                    <IconButton noBg icon={icons.arrowLeft} onClick={handleBack} title="Go back" />
                    <h4>Edit Media</h4>
                </div>
                <div className="options right">
                    <Button
                        icon={icons.save}
                        onClick={handleSubmit}
                        disabled={!hasChanges || saving}
                        secondary={hasChanges}
                    >
                        {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'Saved'}
                    </Button>
                    <IconButton icon={icons.trash} onClick={onDelete} title="Delete media" />
                </div>
            </TopBar>

            <div className="row media-edit-content">

                <div className="column col-5 media-edit-fields">
                    <div className="media-info" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <h5 style={{ marginBottom: '10px' }}>Media Information</h5>
                        {getMediaInfo()}
                    </div>
                    <GenericEditor
                        app={app}
                        organization={organization}
                        title="Media"
                        def={{ ...mediaEditDef, fields: { media: { ...mediaEditDef.fields.media, def: mediaFieldsDef } } }}
                        data={{ media: [editedMedia] }}
                        selection={{
                            0: {
                                field: 'media',
                                fields: mediaFieldsDef.fields,
                                value: editedMedia
                            }
                        }}
                        config={{ app, organization }}
                        onChange={handleChange}
                        noDelete
                    />
                </div>
                
                <div className="column col-7 media-edit-preview">
                    <div className="media-preview-container">
                        {getPreview()}
                    </div>
                </div>
            </div>
            {saving && <div className="media-saving fadeIn">
                <Spinner showAfter={0} />
            </div>}
        </div>
    )
}
